<script lang="ts">
import type { AccordionEmits, AccordionItem, AccordionProps, AccordionSlots } from '#ui/types';
import { tv } from 'tailwind-variants';
import theme from '#build/ui/accordion';
</script>

<script setup lang="ts" generic="T extends AccordionItem">
import { useForwardPropsEmits } from 'reka-ui';
import { computed } from 'vue';
import { useAppConfig } from '#app/config';
import { omit } from '#layers/base/public';
import Accordion from '#ui/components/Accordion.vue';

const { disabled, ui: uiProp, ...forwardedProps } = defineProps<AccordionProps<T>>();
const emits = defineEmits<AccordionEmits>();
const slots = defineSlots<AccordionSlots<T>>();

const forwarded = useForwardPropsEmits(() => ({ ...forwardedProps, disabled, ui: uiProp }), emits);
const ownSlotNames = ['trailing', 'leading'] as const;
const appConfig = useAppConfig();
const ui = computed(() => tv({ extend: tv(theme), ...appConfig.ui?.accordion })({
	disabled,
}));
</script>

<template>
	<Accordion v-bind="forwarded">
		<template #leading="{ item, index, open }">
			<!-- @vue-ignore TODO figure out why this is needed -->
			<slot name="leading" v-bind="{ item, index, open, ui }">
				<DIcon
					:name="trailingIcon ?? 'lucide:chevron-right'"
					:class="ui.leadingIcon({
						class: [
							'size-5 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90',
							uiProp?.leadingIcon,
							item?.ui?.leadingIcon,
						],
					})"
				/>
			</slot>
		</template>
		<template v-for="(_, name) in omit(slots, ownSlotNames)" #[name]="slotData">
			<!-- TODO this might get better with a new Nuxt UI release -->
			<!-- @vue-expect-error -->
			<slot :name v-bind="slotData" />
		</template>
		<template #trailing="{ item, index, open }">
			<span class="ms-auto">
				<!-- @vue-ignore TODO figure out why this is needed -->
				<slot name="trailing" v-bind="{ item, index, open, ui }">
					<DIcon v-if="item.icon" :name="item.icon" class="size-5 shrink-0" />
				</slot>
			</span>
		</template>
	</Accordion>
</template>
